/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { BrandingUiInfoKeys } from '../../core/entities/BrandingUiInfoKeys';
import { DefaultServicesClientImpl } from '../../core/impls/DefaultServiceClientImpl';
import { PreSignInError } from '../../core/error/PreSignInError';
import { DEFAULT_LANGUAGE_INDEX, SUPPORTED_LOCALES } from '../../config';
import { DeviceModel } from '../../core/device/DeviceModel';
import { ErrorTypes } from '../../core/error/ErrorTypes';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import { ScreenTaskControllerTypes } from '../../core/navigation/ScreenTaskControllerTypes';
import { ScreenControllerTypes } from '../../core/navigation/ScreenControllerTypes';
import { Storage } from '../../core/storage/Storage';
import {
    showErrorScreen,
    setScreenTaskController,
    showSplashScreen,
} from '../screens/screens.reducers';
import { PostSignInError } from '../../core/error/PostSignInError';
import { PartialResponseError } from '../../core/error/PartialResponseError';
import { EmptyResponseError } from '../../core/error/EmptyResponseError';
import { VuLogger } from '../../core/logging';
import { IOverlayDialog } from '../overlaydialog/overlaydialogprops';
import { hideOverlayDialog, showOverlayDialog } from '../overlaydialog/overlaydialog.reducers';
import { OverlayTypes } from '../overlay/OverlayTypes';

// GLOBAL_MOUSE_TRAP value to be the same as of file src/store/error/error.reducers.ts
const GLOBAL_MOUSE_TRAP: string = 'HideErrorScreenOverlays';

export const doErorrOperations =
    (code, title, description, userOptions, screenTaskController) =>
    (dispatch: Function, getState: Function) => {
        const { isOverlayDialogActive, uiProperties: uiProps } = getState().overlayDialog;
        const isNoNetworkOverlayActive =
            isOverlayDialogActive && uiProps.overlayType === OverlayTypes.NO_NETWORK;

        if (!isNoNetworkOverlayActive) {
            dispatch(showErrorScreen());
            const overlayDialogProps: IOverlayDialog = {
                description,
                hasDimmer: true,
                hasSeperator: false,
                isPersistent: true,
                messageCode: code,
                mouseTrapValue: GLOBAL_MOUSE_TRAP,
                options: userOptions,
                title,
            };
            dispatch(showOverlayDialog(overlayDialogProps));
            dispatch(setScreenTaskController(screenTaskController));
            VuLogger.logNotifyEvent(screenTaskController, title);
        }
    };

export const sos = (sosErrorCode: string) => (dispatch: Function) => {
    dispatch(
        doErorrOperations(
            sosErrorCode,
            Locale.getString(LocaleKeys.SOS_OVERLAY_TITLE),
            Locale.getString(LocaleKeys.SOS_OVERLAY_DESCRIPTION),
            [{ text: Locale.getString(LocaleKeys.SOS_OVERLAY_OPTION_EXIT) }],
            ScreenTaskControllerTypes.SOS_ERROR_TASKS,
        ),
    );
};

export const languageNotSupportedError = () => (dispatch: Function) => {
    Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).setItem(
        Storage.Keys.LANGUAGE,
        SUPPORTED_LOCALES[DEFAULT_LANGUAGE_INDEX].language,
    );
    Storage.getInstance(Storage.StorageType.LOCAL_STORAGE).setItem(
        Storage.Keys.LOCALE,
        SUPPORTED_LOCALES[DEFAULT_LANGUAGE_INDEX].locale,
    );
    Locale.setLocale(
        DeviceModel.getTVProperties().getLocale(),
        DeviceModel.getTiVoSettings().getMsoName(),
    );

    const mDescription = Locale.getString(
        LocaleKeys.LANGUAGE_NOT_SUPPORTED_OVERLAY_DESCRIPTION,
    ).replace(
        'SUPPORTED_LANGUAGES',
        `${Locale.getString(LocaleKeys.SETTINGS_ENGLISH)}, ${Locale.getString(
            LocaleKeys.SETTINGS_SPANISH,
        )}`,
    );

    dispatch(
        doErorrOperations(
            ErrorTypes.LANGUAGE_NOT_SUPPORTED_OVERLAY_ERROR_D143,
            Locale.getString(LocaleKeys.LANGUAGE_NOT_SUPPORTED_OVERLAY_TITLE),
            mDescription,
            [{ text: Locale.getString(LocaleKeys.OVERLAY_OPTION_OK_TEXT) }],
            ScreenTaskControllerTypes.LANGUAGE_NOT_SUPPORTED_OVERLAY,
        ),
    );
};

export const showTemporaryServiceConnectionIssueOverlay =
    (error: PreSignInError | PostSignInError | PartialResponseError | EmptyResponseError) =>
    (dispatch: Function) => {
        const service = new DefaultServicesClientImpl();
        const customerSupportUrl = service.getBrandingUiInfoForKey(
            BrandingUiInfoKeys.URL_CUSTOMER_SUPPORT,
        );
        const description = error
            .getDescription()
            .replace('CUSTOMER_SUPPORT_URL', customerSupportUrl);

        dispatch(
            doErorrOperations(
                error.getCode(),
                error.getTitle(),
                description,
                [{ text: Locale.getString(LocaleKeys.OVERLAY_OPTION_EXIT_TEXT) }],
                ScreenTaskControllerTypes.TEMPORARY_SERVICE_CONNECTION_ISSUE_OVERLAY,
            ),
        );
    };

export const hideErrorOverlay = () => (dispatch: Function, getState: Function) => {
    const { previousScreenController } = getState().screens;
    dispatch(hideOverlayDialog());
    switch (previousScreenController) {
        case ScreenControllerTypes.SPLASH_SCREEN:
            dispatch(showSplashScreen());
            break;
        default:
    }
};
